import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, API, errMsg } from '../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AcademicRecordCard } from '../components/AcademicRecordCard';
import { ProgressionView } from '../components/acad/ProgressionView';

const AcademicHistoryBlock = ({ studentId }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['gb-hist', studentId], queryFn: () => api.get(`/gb/history/${studentId}`).then((r) => r.data).catch(() => null) });
  const h = data?.history;
  const recalc = async () => {
    try { await api.post(`/gb/history/recalc/${studentId}`); toast.success('History recalculated - inputs logged'); qc.invalidateQueries({ queryKey: ['gb-hist', studentId] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3" data-testid="s360-academic-history">
      <div className="flex items-center justify-between mb-2">
        <p className="font-mono-label text-[10px] uppercase text-slate-400">Academic history - term by term</p>
        <button className="text-xs text-blue-600 underline" data-testid="history-recalc-btn" onClick={recalc}>Recalculate</button>
      </div>
      {!h && <p className="text-sm text-slate-400">Not calculated yet - recalculate to build the term-by-term record.</p>}
      {h && (
        <>
          <p className="text-sm mb-2">Cumulative GPA <b>{h.cumulative_gpa ?? '-'}</b> · {h.credits_earned} of {h.credits_attempted} credits earned · standing <b>{h.standing || '-'}</b></p>
          {(h.terms || []).map((tm) => (
            <div key={tm.term} className="py-1.5 border-t border-slate-50 text-sm" data-testid={`history-term-${tm.term}`}>
              <b>{tm.term}</b> — term GPA {tm.term_gpa ?? '-'} · cumulative {tm.cumulative_gpa ?? '-'} · {tm.credits_earned}/{tm.credits_attempted} credits · <span className="text-xs">{tm.standing}</span>
              <span className="block text-xs text-slate-400">{tm.courses.map((c) => `${c.course_code}: ${c.value}`).join(' · ')}</span>
            </div>
          ))}
          {(data.runs || [])[0] && <p className="text-[10px] text-slate-400 mt-2">Last recalculation by {data.runs[0].run_by} at {(data.runs[0].created_at || '').slice(0, 16).replace('T', ' ')} · inputs hash {data.runs[0].inputs_hash}</p>}
        </>
      )}
    </div>
  );
};

const ACTION_BADGE = { registered: 'bg-green-100 text-green-700', waitlisted: 'bg-amber-100 text-amber-700', seat_offered: 'bg-blue-100 text-blue-700', promoted: 'bg-green-100 text-green-700', dropped: 'bg-slate-100 text-slate-500', withdrawn: 'bg-rose-100 text-rose-600', offer_declined: 'bg-slate-100 text-slate-500', offer_expired: 'bg-slate-100 text-slate-500', hold_placed: 'bg-rose-100 text-rose-600', hold_released: 'bg-green-100 text-green-700' };

const RegistrationHistoryCard = ({ studentId }) => {
  const { data: hist } = useQuery({ queryKey: ['reg-hist', studentId], queryFn: () => api.get(`/reg/history?student_id=${studentId}`).then((r) => r.data).catch(() => []) });
  const { data: holds } = useQuery({ queryKey: ['reg-holds', studentId], queryFn: () => api.get(`/reg/holds?student_id=${studentId}&active_only=true`).then((r) => r.data).catch(() => []) });
  return (
    <div data-testid="s360-registrations">
      {(holds || []).length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 mb-3" data-testid="s360-active-holds">
          <p className="font-mono-label text-[10px] uppercase text-rose-600 mb-1">Active holds - shown first because they block actions</p>
          {holds.map((h) => <p key={h.id} className="text-sm text-rose-700 capitalize">{h.hold_type}: <span className="normal-case">{h.reason}</span> <span className="text-xs">(blocks {(h.blocks || []).join(', ')})</span></p>)}
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {(hist || []).map((h) => (
          <div key={h.id} className="p-3 text-sm flex flex-wrap items-center gap-2" data-testid={`reg-hist-${h.id}`}>
            <span className="font-mono-label text-[10px] text-slate-400">{(h.created_at || '').slice(0, 16).replace('T', ' ')}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded ${ACTION_BADGE[h.action] || 'bg-slate-100'}`}>{h.action.replace(/_/g, ' ')}</span>
            {h.course_code && <span className="font-medium">{h.course_code} {h.section_code}</span>}
            <span className="text-slate-500">by {h.actor}</span>
            {h.reason && <span className="text-xs text-slate-400">— {h.reason}</span>}
            {(h.overrides || []).length > 0 && <span className="font-mono-label text-[9px] uppercase bg-blue-100 text-blue-700 px-2 py-0.5 rounded" title={h.overrides.map((o) => `${o.name}: ${o.reason}`).join('; ')}>override</span>}
          </div>
        ))}
        {(hist || []).length === 0 && <p className="p-4 text-sm text-slate-400">No registration activity yet - every change will appear here with the actor, time and reason.</p>}
      </div>
    </div>
  );
};

export default function Student360() {
  const { id } = useParams();
  const { data, error } = useQuery({ queryKey: ['s360', id], queryFn: () => api.get(`/students/${id}/360`).then((r) => r.data) });
  const { data: exams } = useQuery({ queryKey: ['exams'], queryFn: () => api.get('/exams').then((r) => r.data.filter((e) => e.results_status === 'published')).catch(() => []) });

  if (error) return <p className="text-rose-600" data-testid="s360-error">{errMsg(error)}</p>;
  if (!data) return <p className="text-slate-400">Loading...</p>;
  const s = data.student;
  const attPct = data.attendance.length ? Math.round(data.attendance.filter((a) => a.status !== 'absent').length / data.attendance.length * 100) : null;

  return (
    <div data-testid="student-360-page">
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6 flex flex-wrap items-center gap-6 stagger-in">
        <div className="w-16 h-16 bg-slate-900 text-white rounded-md flex items-center justify-center font-display font-extrabold text-2xl">{s.name[0]}</div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="s360-name">{s.name}</h1>
          <p className="text-sm text-slate-500">Grade {s.grade}{s.section} · {s.admission_no} · DOB {s.dob || '-'}</p>
        </div>
        <div className="ml-auto flex gap-6">
          <div><p className="font-mono-label text-[10px] uppercase text-slate-400">Attendance</p><p className="font-display text-xl font-bold">{attPct ?? '-'}%</p></div>
          <div><p className="font-mono-label text-[10px] uppercase text-slate-400">Classification</p><p className="font-display text-xl font-bold capitalize">{s.classification}</p></div>
        </div>
      </div>

      <Tabs defaultValue="guardians">
        <TabsList className="h-auto flex-wrap">
          {['guardians', 'academics', 'academic record', 'registrations', 'attendance', 'fees', 'consents', 'audit'].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`s360-tab-${t}`} className="capitalize h-auto py-2">{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="academic record" className="mt-4">
          <AcademicRecordCard studentId={id} />
          <AcademicHistoryBlock studentId={id} />
          <div className="bg-white border border-slate-200 rounded-xl p-5 mt-4" data-testid="s360-progression">
            <p className="font-display font-bold">Progression</p>
            <ProgressionView studentId={id} />
          </div>
        </TabsContent>

        <TabsContent value="registrations" className="mt-4">
          <RegistrationHistoryCard studentId={id} />
        </TabsContent>

        <TabsContent value="guardians" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4" data-testid="s360-guardians">
            {data.guardians.map((g) => (
              <div key={g.id} className={`bg-white border rounded-md p-4 ${g.blocked ? 'border-rose-300' : 'border-slate-200'}`}>
                <p className="font-semibold">{g.guardian_name} <span className="text-xs text-slate-400">({g.relation})</span></p>
                <p className="text-xs text-slate-500 mb-2">{g.guardian_email}</p>
                <div className="flex gap-2 flex-wrap">
                  {g.blocked && <span className="font-mono-label text-[9px] uppercase bg-rose-100 text-rose-700 px-2 py-1 rounded" data-testid="guardian-blocked-badge">Blocked: {g.reason}</span>}
                  {!g.blocked && ['can_view_academics', 'can_view_fees', 'can_receive_comms'].map((k) => (
                    <span key={k} className={`font-mono-label text-[9px] uppercase px-2 py-1 rounded ${g[k] ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>{k.replace('can_', '').replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
            ))}
            {data.guardians.length === 0 && <p className="text-slate-400 text-sm">No guardians linked.</p>}
          </div>
        </TabsContent>

        <TabsContent value="academics" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="s360-academics">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr className="text-left">{['Assessment', 'Subject', 'Marks', '%', 'Grade'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}</tr></thead>
              <tbody>{data.marks.map((m) => (
                <tr key={m.id} className="border-t border-slate-100"><td className="py-2 px-3">{m.assessment_name}</td><td className="py-2 px-3">{m.subject}</td><td className="py-2 px-3">{m.marks}/{m.max_marks}</td><td className="py-2 px-3">{m.pct}%</td><td className="py-2 px-3 font-bold">{m.grade}</td></tr>
              ))}</tbody>
            </table>
            {data.marks.length === 0 && <p className="p-4 text-sm text-slate-400">No marks recorded (or academics hidden by guardian access flags).</p>}
          </div>
          {data.report_cards.length > 0 && (
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              {data.report_cards.map((r) => (
                <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-4">
                  <p className="font-semibold">{r.term} <span className={`text-xs px-2 py-0.5 rounded ml-2 ${r.status === 'released' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{r.status}</span></p>
                  <p className="font-display text-2xl font-bold mt-1">{r.overall_pct}%</p>
                </div>
              ))}
            </div>
          )}
          {(exams || []).filter((e) => String(e.grade) === String(s.grade)).length > 0 && (
            <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4" data-testid="s360-exam-reportcards">
              <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Exam term report cards</p>
              <div className="flex gap-2 flex-wrap">
                {(exams || []).filter((e) => String(e.grade) === String(s.grade)).map((e) => (
                  <a key={e.id} data-testid={`s360-reportcard-${e.id}`} href={`${API}/exams/${e.id}/report-cards.pdf?student_id=${id}`}
                    className="text-xs border border-orange-200 text-orange-700 rounded px-2.5 py-1.5 hover:bg-orange-50">{e.name} · {e.term} PDF</a>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="attendance" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-2" data-testid="s360-attendance">
            {data.attendance.map((a) => (
              <div key={a.id} title={`${a.date}: ${a.status}`} className={`w-8 h-8 rounded flex items-center justify-center text-[10px] font-mono-label ${a.status === 'present' ? 'bg-green-100 text-green-800' : a.status === 'late' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-700'}`}>{a.date.slice(8)}</div>
            ))}
            {data.attendance.length === 0 && <p className="text-sm text-slate-400">No attendance records.</p>}
          </div>
        </TabsContent>

        <TabsContent value="fees" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="s360-fees">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr className="text-left">{['Plan', 'Total', 'Paid', 'Balance', 'Status'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}</tr></thead>
              <tbody>{data.invoices.map((i) => (
                <tr key={i.id} className="border-t border-slate-100"><td className="py-2 px-3">{i.plan_name || '-'}</td><td className="py-2 px-3">₹{(i.total ?? 0).toLocaleString()}</td><td className="py-2 px-3">₹{(i.paid ?? 0).toLocaleString()}</td><td className="py-2 px-3 font-bold">₹{(i.balance ?? 0).toLocaleString()}</td><td className="py-2 px-3">{i.status}</td></tr>
              ))}</tbody>
            </table>
            {data.invoices.length === 0 && <p className="p-4 text-sm text-slate-400">No invoices (or fees hidden by access flags).</p>}
          </div>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mt-3">Hard rule: arrears never block academic or safeguarding records</p>
        </TabsContent>

        <TabsContent value="consents" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4" data-testid="s360-consents">
            {data.consents.map((c) => (
              <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-4">
                <p className="font-semibold capitalize">{c.type.replace(/_/g, ' ')}</p>
                <p className="text-xs text-slate-500">set by {c.set_by} ({c.set_by_role})</p>
                <span className={`inline-block mt-2 font-mono-label text-[9px] uppercase px-2 py-1 rounded ${c.status === 'granted' ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-700'}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="s360-audit">
            {data.audit.map((a) => (
              <div key={a.id} className="p-3 text-sm flex flex-wrap gap-2 items-center">
                <span className="font-mono-label text-[10px] text-slate-400">{a.ts?.slice(0, 19)}</span>
                <span className="font-medium">{a.action}</span>
                <span className="text-slate-500">by {a.actor_email}</span>
                {a.ai_involved && <span className="font-mono-label text-[9px] uppercase bg-blue-100 text-blue-700 px-2 py-0.5 rounded">AI</span>}
              </div>
            ))}
            {data.audit.length === 0 && <p className="p-4 text-sm text-slate-400">Audit trail visible to admins and registrar only.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
